import { TABLE_TYPE_IDS, TableColumnPreferences } from '@/lib/models/cookie-models';

// Safe wrappers around localStorage to avoid exceptions (SSR, privacy mode, quota)
function lsGet(key: string): string | null {
	try {
		if (typeof window === 'undefined' || !('localStorage' in window)) return null;
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function lsSet(key: string, value: string): void {
	try {
		if (typeof window === 'undefined' || !('localStorage' in window)) return;
		window.localStorage.setItem(key, value);
	} catch {
		// noop
	}
}

function lsRemove(key: string): void {
	try {
		if (typeof window === 'undefined' || !('localStorage' in window)) return;
		window.localStorage.removeItem(key);
	} catch {
		// noop
	}
}

/**
 * Clears all known app keys (TABLE_TYPE_IDS) from localStorage.
 * No-op in non-browser environments or when storage is unavailable.
 */
export function lsClear(): void {
	Object.values(TABLE_TYPE_IDS).forEach((key) => {
		lsRemove(key);
	});
}

/**
 * Persist table column preferences for a given table type in localStorage.
 * Overwrites any previously stored preferences for this key.
 * @param tableTypeId Storage key corresponding to the table type
 * @param columns The column preference object to persist
 * @returns The saved column preferences
 */
export function storeTableColumnPreferences<K extends keyof TableColumnPreferences>(
	tableTypeId: K,
	columns: NonNullable<TableColumnPreferences[K]>
): TableColumnPreferences[K] {
	lsSet(tableTypeId, JSON.stringify(columns));
	return columns;
}

/**
 * Return table columns with defaults overlaid by any user preferences from localStorage.
 * If no valid preferences are stored, returns a shallow copy of the defaults.
 * @param tableTypeId Storage key corresponding to the table type
 * @param defaultColumns The static default column configuration for the table
 * @returns The merged column configuration (defaults + stored preferences)
 */
export function getMergedTableColumns<K extends keyof TableColumnPreferences>(
	tableTypeId: K,
	defaultColumns: NonNullable<TableColumnPreferences[K]>
): NonNullable<TableColumnPreferences[K]> {
	let userPrefs: unknown;
	try {
		const storedValue = lsGet(tableTypeId);
		if (storedValue) userPrefs = JSON.parse(storedValue);
	} catch {}
	if (
		userPrefs &&
		typeof userPrefs === 'object' &&
		!Array.isArray(userPrefs) &&
		Object.keys(userPrefs as object).length > 0
	) {
		const merged = { ...defaultColumns } as Record<string, { isHidable?: boolean }>;
		const prefs = userPrefs as Record<string, { isHidable?: boolean }>;
		for (const columnId of Object.keys(prefs)) {
			if (columnId in merged && merged[columnId].isHidable) {
				merged[columnId] = prefs[columnId];
			}
		}
		return merged as NonNullable<TableColumnPreferences[K]>;
	}
	return { ...defaultColumns };
}
